/**
 * Main entry point for the Filegrant React File Manager application.
 * - Sets up Sentry for monitoring when not on localhost and VITE_APP_SENTRY_DSN is set
 * - Renders the application tree with Ant Design, React Router, and multiple context providers
 */

import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';

import { MsalProvider } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import { type ReactNode } from 'react';
import App from './App';
import AntdConfigProvider from './components/Core/AntdConfigProvider';
import { BrandingProvider } from './contexts/BrandingContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { FileUploadProvider } from './contexts/FileUploadContext';
import { TagsProvider } from './contexts/TagsContext';
import { DocumentTitleSync } from './lib/documentTitle';
import { msalInstance } from './lib/msalConfig';
import { useAppStore } from './store/store';
import './lib/fileDownload/fileDownloadPollingBootstrap';

// Global styles and other required imports
import 'overlayscrollbars/styles/overlayscrollbars.css';
import './index.css';
import './utils/i18n';

/**
 * Wrapper component that prevents rendering of data-fetching providers during logout or login redirect
 * This prevents unnecessary API calls when the user is logging out or already authenticated on login page with aiGrant param
 */
function DataProvidersWrapper({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authenticatedUser = useAppStore((s) => s.authenticatedUser);
  const token = useAppStore((s) => s.token);
  const isAuthenticated = !!(authenticatedUser && token);

  // Check if aiGrant parameter is set to '1'
  const hasAiGrantParam = searchParams.get('aiGrant') === '1';

  // If on logout page, don't render the data providers to prevent API calls
  if (location.pathname === '/logout') {
    return <>{children}</>;
  }

  // If on login page, already authenticated, AND has aiGrant=1 parameter
  // don't render the data providers. This prevents API calls when redirecting to AIGrant.
  // Without aiGrant param, we render providers normally (user manually navigated to /login)
  if (location.pathname === '/login' && isAuthenticated && hasAiGrantParam) {
    return <>{children}</>;
  }

  // Otherwise, render all data providers normally
  return (
    <TagsProvider>
      <PermissionsProvider>
        <FileUploadProvider>{children}</FileUploadProvider>
      </PermissionsProvider>
    </TagsProvider>
  );
}

/**
 * Root layout: must live under `RouterProvider` so hooks like `useBlocker` (in UnsavedChangesProvider) work.
 */
function RootLayout() {
  return (
    <AntdConfigProvider>
      <MsalProvider instance={msalInstance}>
        <UnsavedChangesProvider>
          <BrandingProvider>
            <AuthProvider>
              <DataProvidersWrapper>
                <DocumentTitleSync />
                <Outlet />
              </DataProvidersWrapper>
            </AuthProvider>
          </BrandingProvider>
        </UnsavedChangesProvider>
      </MsalProvider>
    </AntdConfigProvider>
  );
}

// Localhost: never send telemetry to Sentry
const isLocalhost = window.location.hostname === 'localhost';

const sentryDsn = (import.meta.env.VITE_APP_SENTRY_DSN as string | undefined)?.trim() ?? '';
const shouldInitSentry = !isLocalhost && sentryDsn.length > 0;

if (shouldInitSentry) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    environment: import.meta.env.VITE_APP_ENV_MODE,
    tracesSampleRate: 1.0, // Capture 100% of the transactions for tracing
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions for session replay by default
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions where errors occur
    profileSessionSampleRate: 1.0, // Profile every session (Sentry SDK v11 renamed from profilesSampleRate)
    // For distributed tracing: configure 'tracePropagationTargets' as needed
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  });
}

/**
 * `createBrowserRouter` is required for `useBlocker` (unsaved-changes guard).
 * A splat-only child (`path: '*'`) does not match the bare `/` URL, so the root redirect
 * must be an `index` route here — not only inside `<App />` routes.
 */
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    path: '/',
    children: [
      {
        index: true,
        element: <Navigate replace to="/login" />,
      },
      {
        element: <App />,
        path: '*',
      },
    ],
  },
]);

// Reuse the same React root across Vite HMR reloads (avoids "createRoot() on a container that already has a root")
const rootContainer = document.getElementById('root') as HTMLElement;
type ReactDomRoot = ReturnType<typeof createRoot>;
type RootElement = HTMLElement & { __filegrantReactRoot?: ReactDomRoot };

const rootElement = rootContainer as RootElement;
let reactRoot = rootElement.__filegrantReactRoot;

if (!reactRoot) {
  reactRoot = createRoot(rootContainer);
  rootElement.__filegrantReactRoot = reactRoot;
}

// Data router is required for `useBlocker` (unsaved-changes navigation guard)
reactRoot.render(<RouterProvider router={router} />);
